import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { ToastQueue } from 'base/ui/toast';
import { BlocksService, OwnershipType } from 'blocks/blocks_service';
import { ProjectsDataStore } from './projects_data_store';

// Singleton manager instance that persists across the app lifecycle
let storeManagerInstance: ProjectsDataStoreManager | null = null;

type StoreCacheKey = `ownership-${OwnershipType}`;

/**
 * Gets the appropriate ProjectsDataStore for the given ownership type.
 * Uses a caching mechanism to preserve state when switching between tabs.
 */
export function getProjectsDataStore({
  blocksService,
  accountStore,
  analytics,
  toastQueue,
  ownershipType = 'all',
}: {
  blocksService: BlocksService;
  accountStore: AccountStore;
  analytics?: AnalyticsService;
  toastQueue?: ToastQueue;
  ownershipType?: OwnershipType | 'all';
}): ProjectsDataStore {
  // Initialize the manager if it doesn't exist yet
  if (!storeManagerInstance) {
    storeManagerInstance = new ProjectsDataStoreManager({
      blocksService,
      accountStore,
      analytics,
      toastQueue,
    });
  }

  // Get the appropriate store for the current ownership type
  return storeManagerInstance.getStore({ ownershipType });
}

// Common dependencies that are shared across all store instances
type StoreDependencies = {
  blocksService: BlocksService;
  accountStore: AccountStore;
  analytics?: AnalyticsService;
  toastQueue?: ToastQueue;
};

// Filter options that determine which store to use
type StoreFilterOptions = {
  ownershipType?: OwnershipType | 'all';
  // Future: add more filter options here as needed (tags, search, etc.)
};

export class ProjectsDataStoreManager {
  // Cache of store instances by key
  private storeCache: Map<StoreCacheKey, ProjectsDataStore> = new Map();

  // Dependencies shared across all stores
  private dependencies: StoreDependencies;

  constructor(dependencies: StoreDependencies) {
    this.dependencies = dependencies;
  }

  /**
   * Gets an existing store from cache or creates a new one for the given filter options
   */
  getStore(options: StoreFilterOptions): ProjectsDataStore {
    const key = this.generateStoreKey(options);
    // Create a new store if one doesn't exist for this key
    if (!this.storeCache.has(key)) {
      // Merge common dependencies with filter options
      const store = new ProjectsDataStore({
        ...this.dependencies,
        ...options,
      });
      this.storeCache.set(key, store);
    }

    return this.storeCache.get(key)!;
  }

  /**
   * Invalidates a specific store based on filter options
   * This forces the store to be recreated next time it's requested
   */
  invalidateStore(options: StoreFilterOptions): void {
    const key = this.generateStoreKey(options);
    this.storeCache.delete(key);
  }

  /**
   * Invalidates all stores in the cache
   * Use this when something affects all projects (e.g., global settings change)
   */
  invalidateAll(): void {
    this.storeCache.clear();
  }

  /**
   * Generates a unique cache key for the given filter options
   * This is designed for extensibility - new filter types can be added
   * without breaking existing functionality
   */
  private generateStoreKey(options: StoreFilterOptions): StoreCacheKey {
    const { ownershipType = 'all' } = options;
    // Return a tuple with all the filter options
    return `ownership-${ownershipType}`;
  }
}

/**
 * Helper to invalidate stores after operations that affect projects.
 * - If no options provided, invalidates all stores
 * - If ownership type specified, only invalidates that specific store
 */
export function invalidateProjectsStore(options?: {
  ownershipType?: OwnershipType | 'all';
}) {
  if (!storeManagerInstance) return;

  if (options) {
    storeManagerInstance.invalidateStore(options);
  } else {
    storeManagerInstance.invalidateAll();
  }
}
